import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface WidgetUpgradeCTAProps {
  /** Optional heading override; falls back to a generic "available on a paid plan". */
  title?: string;
  /** Optional body override. */
  description?: string;
  /** Extra class names (e.g. `stat-card` to blend into the dashboard grid). */
  className?: string;
}

/**
 * Locked-widget placeholder shown in place of a plan-gated widget.
 *
 * Rendered both pre-emptively (when the mobile feature flag is off) and as a
 * safety net when a gated endpoint returns 403 `subscription_upgrade_required`.
 * The button routes to the mobile subscription purchase / change-plan flow;
 * after a successful upgrade the buy flow refetches `/subscriptions`, the flags
 * flip to `"true"` and the real widget reappears.
 */
const WidgetUpgradeCTA: React.FC<WidgetUpgradeCTAProps> = ({
  title,
  description,
  className = '',
}) => {
  const { t } = useTranslation();

  return (
    <div className={`widget-upgrade-cta ${className}`.trim()}>
      <div className="widget-upgrade-cta__lock" aria-hidden>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
          <rect x="4" y="10" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="1.75" />
          <path
            d="M8 10V7a4 4 0 0 1 8 0v3"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <h3 className="widget-upgrade-cta__title">
        {title ?? t('widget_upgrade_cta_title')}
      </h3>
      <p className="widget-upgrade-cta__text">
        {description ?? t('widget_upgrade_cta_description')}
      </p>
      <Link to="/licenses/buy?plugin=mobile" className="widget-upgrade-cta__button">
        {t('widget_upgrade_cta_button')}
      </Link>
    </div>
  );
};

export default WidgetUpgradeCTA;
